import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import { Header } from "components/header";
import { Footer } from "components/footer/footer";
import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";
import stores from "consts/stores.json";
import { getQueryParams, useCurrentLocation } from "lib/utils";
import AuthContext from "contexts/AuthContext";
import ScrollToTop from "components/scroll-to-top";
import { Link, useLocation } from "react-router-dom";
import useIsMobile from "hooks/useIsMobile";
import { TradeInIcon } from "pages/svgs/tradein-logo";
import { COUNTRY_NAME, LOCALE_NAME, STATE_NAME } from "consts/query-params";
import useScrollToAnchor from "hooks/useScrollToAnchor";
import LegoWoman from "assets/lego-woman.png";
import { EVENT_7, EVENT_18, EVENT_19 } from "utils/tracking";
import useWhen from "hooks/useWhen";
import { END_OF_PILOT_25_ARGS } from "consts/feature-config";

const FAQ = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { location: currentLocation } = useCurrentLocation();
  const queryParams = getQueryParams(location.search);
  const [takeBackTypeToggle, setTakeBackTypeToggle] = useState(
    queryParams?.preselected === "in-store" ? "store" : "mail"
  );

  const [showInStore, setShowInStore] = useState(false);

  // Accordion triggers/components (copied from preparation guidelines for consistency)
  const AccordionTrigger = React.forwardRef(
    ({ children, className, ...props }, forwardedRef) => (
      <Accordion.Header className="AccordionHeader text-primary text-2xl">
        <Accordion.Trigger
          className={classNames("AccordionTrigger text-left", className)}
          {...props}
          ref={forwardedRef}
        >
          <div>{children}</div>
          <span
            className="AccordionChevron material-symbols-outlined align-bottom ml-large text-secondary"
            aria-hidden
          >
            keyboard_arrow_down
          </span>
        </Accordion.Trigger>
      </Accordion.Header>
    )
  );

  const AccordionContent = React.forwardRef(
    ({ children, className, ...props }, forwardedRef) => (
      <Accordion.Content
        className={classNames("AccordionContent", className)}
        {...props}
        ref={forwardedRef}
      >
        <div className="AccordionContentText text-secondary mb-regular">
          {children}
        </div>
      </Accordion.Content>
    )
  );

  const store = currentLocation?.takebackMethod?.find((method) =>
    method.startsWith("in_store")
  )
    ? stores.find(
        (store) =>
          store.value ===
          currentLocation.takebackMethod.find((method) =>
            method.startsWith("in_store")
          )
      )
    : null;
  const storeLocation = store?.name;
  const storeAddress = store?.address;

  const updateShowInStore = useCallback(() => {
    setShowInStore(
      currentLocation.takebackMethod.some((method) =>
        stores.some((store) => store.value === method)
      )
    );
  }, [currentLocation]);

  useEffect(() => {
    updateShowInStore();
  }, [updateShowInStore]);

  // Handle anchor scrolling when page loads
  useScrollToAnchor();

  const endOfPilot25 = useWhen(END_OF_PILOT_25_ARGS);

  // Determine FAQ variant based on state and tab selection
  const getFAQVariant = () => {
    if (!showInStore || takeBackTypeToggle === "mail") {
      return "mail-in"; // Mail-in: when page is not state specific OR tab switcher is on mail
    }

    if (takeBackTypeToggle === "store") {
      const state = currentLocation?.state;
      if (state === "CO" || state === "AR") {
        return "collection-kiosk"; // Store collection (kiosk): when state CO or AR AND switcher on store
      }
      if (state === "NJ" || state === "PA") {
        return "drop-off-station"; // Drop-off station: when state NJ or PA AND switcher on store
      }
    }

    return "mail-in"; // Default fallback
  };

  const faqVariant = getFAQVariant();

  const { user } = useContext(AuthContext);

  const isMobile = useIsMobile();
  const navigationLink = `${t("urls.my_trade_ins")}/${location.search}`;
  const mobileContent = (
    <>
      <Link to={navigationLink}>
        <TradeInIcon />
      </Link>
    </>
  );

  const CtaButton = ({ className, additionalTracking = [] }) => {
    return (
      <Button
        tracking={[EVENT_7, ...additionalTracking]}
        href={`${t("urls.trade_in_weight")}`}
        queryParams={{
          [LOCALE_NAME]: currentLocation.locale,
          [STATE_NAME]: currentLocation.state,
          [COUNTRY_NAME]: currentLocation.country,
        }}
        className={className}
      >
        {t("landing.button_start_new")}
      </Button>
    );
  };

  // minimise Intercom messenger
  useEffect(() => {
    if (window.Intercom) {
      window.Intercom("hide");
    }
  }, []);

  return (
    <div>
      <ScrollToTop />
      <style>{`
        /* Only show bottom border on last accordion item when closed */
        .AccordionTrigger {
          border-bottom: none !important;
        }
        
        /* Show bottom border on last accordion item when it's closed */
        [data-radix-accordion-item]:last-child[data-state="closed"] .AccordionTrigger {
          border-bottom: 1px solid #d1d5db !important;
        }
      `}</style>
      <Header mobileChildren={mobileContent} classNames="bg-yellow border-none">
        <div className="flex gap-large">
          {!endOfPilot25 && (
            <CtaButton
              className="hidden lg:block"
              additionalTracking={[EVENT_18]}
            />
          )}

          {!isMobile && (
            <Button
              href={user ? `${t("urls.my_trade_ins")}` : `${t("urls.login")}`}
              variant="secondary_no_border"
              queryParams={queryParams}
            >
              {user
                ? t("landing.button_my_trade_ins")
                : t("landing.button_sign_in")}
            </Button>
          )}
        </div>
      </Header>
      <div className="py-12 bg-blue-300/25 flex flex-col items-center">
        <h1 className="text-primary text-[30px] md:text-[40px] font-bold text-center py-medium">
          FAQ
        </h1>
        <p className="text-xl max-w-lg text-center text-secondary">
          Learn how to prepare your trade-in and more details on the LEGO Brick
          Take Back program.
        </p>
      </div>

      <div id="howItWorks" className="">
        <div className="flex flex-col justify-center px-x-large md:px-xx-large ">
          {showInStore ? (
            <div className="mt-xx-large md:mt-xxx-large">
              <div className="mb-x-large md:mb-xx-large pb-xxx-large">
                <h3 className="text-primary text-2xl font-bold md:text-4xl text-center">
                  Choose your trade-in method to view relevant FAQs:
                </h3>
                <div className="flex flex-col md:flex-row lg:max-w-4xl mx-auto mt-regular md:mt-large bg-gray-300/25 border border-gray-300 rounded-xl p-x-small text-primary">
                  <button
                    onClick={() => setTakeBackTypeToggle("mail")}
                    className={`${
                      takeBackTypeToggle === "mail" ? "bg-blue text-white " : ""
                    }rounded-lg py-regular px-x-large text-2xl flex flex-1 items-center justify-center`}
                  >
                    <span
                      className={`material-symbols-outlined align-bottom mr-large scale-125 ${
                        takeBackTypeToggle === "mail"
                          ? "text-white"
                          : "text-[#757575]"
                      }`}
                    >
                      package_2
                    </span>
                    <span className="block truncate leading-10">
                      {t("landing.method_mail")}
                    </span>
                  </button>
                  <button
                    onClick={() => setTakeBackTypeToggle("store")}
                    className={`${
                      takeBackTypeToggle === "store"
                        ? "bg-blue text-white "
                        : ""
                    }rounded-lg py-regular px-x-large text-2xl flex flex-1 items-center justify-center`}
                  >
                    <span
                      className={`material-symbols-outlined align-bottom mr-large scale-125 ${
                        takeBackTypeToggle === "store"
                          ? "text-white"
                          : "text-[#757575]"
                      }`}
                    >
                      storefront
                    </span>
                    <span className="block truncate leading-10">
                      {t("landing.method_store", {
                        store: storeLocation,
                        asterisk: takeBackTypeToggle === "store" ? "*" : "",
                      })}
                    </span>
                  </button>
                </div>

                {takeBackTypeToggle === "store" && (
                  <div className="text-black-300 w-full text-center mt-large text-sm">
                    <span className="font-semibold ">*Address:</span>{" "}
                    {storeAddress}
                  </div>
                )}
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>

      <div className="px-x-large md:px-xx-large">
        <div className="flex flex-col justify-center lg:max-w-6xl mx-auto">
          <div className="mt-xx-large md:mt-xxx-large">
            <Accordion.Root type="single" defaultValue="faq-1" collapsible>
              {!(showInStore && faqVariant !== "mail-in") && (
                <>
                  <Accordion.Item
                    className="gap-0 border border-[#e4e4e4]"
                    value="faq-end-1"
                  >
                    <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                      <div className="text-2xl">
                        If I created a trade-in before October 19th 2025, can I
                        still send in my LEGO bricks?
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="bg-gray-300/25 p-4">
                      <div className="flex flex-col space-y-4 py-x-large">
                        <p>
                          If you created your trade-in between September 2nd and
                          October 19th 2025, you may still participate through
                          October 19th. Your LEGO bricks must be dropped off by
                          October 19th 2025 to participate in this pilot
                          program.
                        </p>
                      </div>
                    </AccordionContent>
                  </Accordion.Item>

                  <Accordion.Item
                    className="gap-0 border border-[#e4e4e4]"
                    value="faq-end-2"
                  >
                    <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                      <div className="text-2xl">
                        Will I still receive my reward if my LEGO bricks have
                        been sent but not yet verified by October 19th 2025?
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="bg-gray-300/25 p-4">
                      <div className="flex flex-col space-y-4 py-x-large">
                        <p>
                          As long as your trade-in is dropped off by October
                          19th 2025, you'll receive your reward once your LEGO
                          bricks have been verified.
                        </p>
                      </div>
                    </AccordionContent>
                  </Accordion.Item>
                </>
              )}

              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-end-3"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    How long will I have access to the My Trade-Ins dashboard to
                    view my trade-in details?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    <p>
                      You'll have access to your{" "}
                      <Link
                        to={t("urls.my_trade_ins")}
                        className="cursor-pointer text-blue"
                      >
                        My Trade-Ins dashboard
                      </Link>{" "}
                      until January 31st 2026. Once your LEGO bricks have been
                      verified, your trade-in receipt and LEGO e-Gift Card are
                      also sent by email for your records.
                    </p>
                  </div>
                </AccordionContent>
              </Accordion.Item>

              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-1"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    Who can participate in the LEGO® Brick Take Back program?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    <p>
                      Anyone in the U.S. can participate and earn a reward! Here
                      are the requirements:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        A valid U.S. ZIP code (we'll confirm eligibility during
                        sign-up)
                      </li>
                      <li>
                        Be 18 or older (or have a parent or guardian sign up if
                        you're under 18)
                      </li>
                      <li>
                        Your personal account (this program is for individuals,
                        not businesses)
                      </li>
                    </ul>
                    <p>
                      Want more details? See the{" "}
                      <Link
                        to={{
                          pathname: t("urls.terms_and_conditions"),
                          search: `?${new URLSearchParams(queryParams).toString()}`,
                        }}
                        className="text-blue cursor-pointer"
                      >
                        Terms and Conditions
                      </Link>
                      .
                    </p>
                  </div>
                </AccordionContent>
              </Accordion.Item>

              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-2"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    What do bricks in "good condition" mean?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    <p>We only accept LEGO® bricks that are:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Clean</li>
                      <li>Undamaged</li>
                      <li>Fully disassembled</li>
                    </ul>
                    <p>
                      View the{" "}
                      <Link
                        to={{
                          pathname: t("urls.preparation_guidelines"),
                          search: `?${new URLSearchParams(queryParams).toString()}`,
                        }}
                        className="text-blue cursor-pointer"
                      >
                        Preparation Guidelines
                      </Link>{" "}
                      for more details.
                    </p>
                  </div>
                </AccordionContent>
              </Accordion.Item>

              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-3"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    How should I prepare my LEGO bricks for trade-in?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    <ol className="list-decimal pl-6 space-y-4">
                      <li>
                        Check that your LEGO® bricks are clean, undamaged, and
                        fully disassembled.{" "}
                        <Link
                          to={{
                            pathname: t("urls.preparation_guidelines"),
                            search: `?${new URLSearchParams(queryParams).toString()}`,
                          }}
                          className="text-blue cursor-pointer"
                        >
                          See what we accept
                        </Link>
                      </li>
                      <li>
                        Weigh your bricks and enter the estimated weight when
                        you start your trade-in.{" "}
                        {!endOfPilot25 && (
                          <Link
                            to={{
                              pathname: t("urls.trade_in_weight"),
                              search: `?${new URLSearchParams(queryParams).toString()}`,
                            }}
                            className="text-blue cursor-pointer"
                          >
                            Start a Trade-In
                          </Link>
                        )}
                      </li>
                      {faqVariant === "mail-in" && (
                        <li>
                          Pack them up in a sturdy cardboard shipping box (up to
                          25 lbs) and seal it with strong tape. Print a free
                          shipping label or generate a shipping QR code and take
                          your package to the nearest UPS location.
                          <br />
                          <strong>More than 25 lbs?</strong> No problem — just
                          create a new trade-in for each 25 lb box!
                        </li>
                      )}
                      {faqVariant === "collection-kiosk" && (
                        <li>
                          Then choose how to return them:
                          <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>
                              <strong>Mail-in:</strong> Pack your bricks up in a
                              sturdy cardboard shipping box (up to 25 lbs) and
                              seal it with strong tape. Once you've sealed your
                              trade-in, print a free shipping label or generate
                              a shipping QR code and take your package to the
                              nearest UPS location.
                              <ul className="list-disc pl-6 mt-1">
                                <li>
                                  More than 25 lbs? No problem — just create a
                                  new trade-in for each 25 lb box!
                                </li>
                              </ul>
                            </li>
                            <li>
                              <strong>In-Store:</strong> No box needed! Just
                              bring your bricks to the store in any container.
                              Our Brick Specialists will help weigh them and
                              guide you through the kiosk steps. Bring your
                              mobile device to confirm your details during the
                              appointment.
                            </li>
                          </ul>
                        </li>
                      )}
                      {faqVariant === "drop-off-station" && (
                        <li>
                          Then choose how to return them:
                          <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>
                              <strong>Mail-in:</strong> Pack your bricks up in a
                              sturdy cardboard shipping box (up to 25 lbs) and
                              seal it with strong tape. Once you've sealed your
                              trade-in, print a free shipping label or generate
                              a shipping QR code and take your package to the
                              nearest UPS location.
                              <ul className="list-disc pl-6 mt-1">
                                <li>
                                  More than 25 lbs? No problem — just create a
                                  new trade-in for each 25 lb box!
                                </li>
                              </ul>
                            </li>
                            <li>
                              <strong>In-Store:</strong> Pack your bricks up in
                              a sturdy cardboard shipping box (up to 25 lbs), no
                              need to seal. Just bring your bricks to your
                              scheduled appointment time and have your check-in
                              QR code ready to show to a Brick Specialist.
                              <ul className="list-disc pl-6 mt-1">
                                <li>
                                  More than 25 lbs? No problem — just create
                                  another trade-in for the second box and bring
                                  both packages to the store.
                                </li>
                                <li>
                                  Bring your mobile device to confirm your
                                  details during the appointment. Each box takes
                                  about 15 minutes to process.
                                </li>
                              </ul>
                            </li>
                          </ul>
                        </li>
                      )}
                    </ol>
                  </div>
                </AccordionContent>
              </Accordion.Item>

              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-4"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    What packaging should I use for my LEGO bricks?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    {faqVariant === "mail-in" && (
                      <>
                        <ul className="list-disc pl-6 space-y-2">
                          <li>
                            Use a sturdy cardboard shipping box that can handle
                            the journey
                          </li>
                          <li>
                            LEGO® bricks should be loose – no plastic bags or
                            packing materials
                          </li>
                          <li>Avoid excess empty space</li>
                          <li>Seal tightly with strong packing tape</li>
                          <li>
                            Use only the provided shipping label or shipping QR
                            code
                          </li>
                        </ul>
                        <p>
                          Each Trade-In needs its own box and unique shipping
                          label or shipping QR code. The LEGO Group isn't
                          responsible for any lost or damaged items caused by
                          insufficient packaging.
                        </p>
                      </>
                    )}
                    {(faqVariant === "collection-kiosk" ||
                      faqVariant === "drop-off-station") && (
                      <>
                        <p>
                          If you decide to mail your Trade-In, please use a
                          sturdy cardboard shipping box that can handle the
                          journey.
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                          <li>
                            LEGO® bricks should be loose – no plastic bags or
                            packing materials
                          </li>
                          <li>Avoid excess empty space</li>
                          <li>Seal tightly with strong packing tape</li>
                          <li>
                            Use only the provided shipping label or shipping QR
                            code
                          </li>
                        </ul>
                        <p>
                          Each Trade-In needs its own box and unique shipping
                          label or shipping QR code. The LEGO Group isn't
                          responsible for any lost or damaged items caused by
                          insufficient packaging.
                        </p>
                        {faqVariant === "collection-kiosk" && (
                          <p>
                            If you'd like to come to a participating store, no
                            shipping box is needed. Just bring your bricks in
                            any easy-to-carry container. You can confirm the
                            participating stores when you create your trade-in.
                          </p>
                        )}
                        {faqVariant === "drop-off-station" && (
                          <p>
                            If you'd like to come to a participating store, use
                            a sturdy box but don't seal it or add a label. Our
                            Brick Specialists will handle this for you. You can
                            confirm the participating stores when you create
                            your trade-in.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </AccordionContent>
              </Accordion.Item>

              {/* Non-accepted bricks questions - conditional based on variant */}
              {faqVariant === "mail-in" && (
                <Accordion.Item
                  className="gap-0 border border-[#e4e4e4]"
                  value="faq-5"
                >
                  <AccordionTrigger className="justify-between w-full px-4 py-5 flex items-center">
                    <div className="text-2xl">
                      {showInStore
                        ? "What happens if LEGO bricks are not accepted after mailing in?"
                        : "What happens if LEGO bricks are not accepted?"}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="bg-gray-300/25 p-4">
                    <div className="flex flex-col space-y-4 py-x-large">
                      <p>
                        Only clean, undamaged, and fully disassembled LEGO®
                        bricks qualify for rewards. If something doesn't meet
                        the guidelines, we'll reward you for the eligible items,
                        but we won't be able to return any non-eligible items.
                        By participating in the program, you agree to our
                        evaluation and transfer ownership of all items sent.
                      </p>
                      <p>
                        <Link
                          to={{
                            pathname: t("urls.preparation_guidelines"),
                            search: `?${new URLSearchParams(queryParams).toString()}`,
                          }}
                          className="text-blue cursor-pointer"
                        >
                          Review what's accepted
                        </Link>{" "}
                        and the{" "}
                        <Link
                          to={{
                            pathname: t("urls.terms_and_conditions"),
                            search: `?${new URLSearchParams(queryParams).toString()}`,
                          }}
                          className="text-blue cursor-pointer"
                        >
                          Terms and Conditions
                        </Link>
                      </p>
                    </div>
                  </AccordionContent>
                </Accordion.Item>
              )}

              {/* Collection kiosk and drop-off station variants */}
              {(faqVariant === "collection-kiosk" ||
                faqVariant === "drop-off-station") && (
                <>
                  <Accordion.Item
                    className="gap-0 border border-[#e4e4e4]"
                    value="faq-5a"
                  >
                    <AccordionTrigger className="justify-between w-full px-4 py-5 flex items-center">
                      <div className="text-2xl">
                        What happens if LEGO bricks are not accepted?
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="bg-gray-300/25 p-4">
                      <div className="flex flex-col space-y-4 py-x-large">
                        <p>
                          Only clean, undamaged, and fully disassembled LEGO®
                          bricks qualify for rewards. If something doesn't meet
                          the guidelines, we'll reward you for the eligible
                          items, but we won't be able to return any non-eligible
                          items. By participating in the program, you agree to
                          our evaluation and transfer ownership of all items
                          sent.
                        </p>
                        <p>
                          <Link
                            to={{
                              pathname: t("urls.preparation_guidelines"),
                              search: `?${new URLSearchParams(queryParams).toString()}`,
                            }}
                            className="text-blue cursor-pointer"
                          >
                            Review what's accepted
                          </Link>{" "}
                          and the{" "}
                          <Link
                            to={{
                              pathname: t("urls.terms_and_conditions"),
                              search: `?${new URLSearchParams(queryParams).toString()}`,
                            }}
                            className="text-blue cursor-pointer"
                          >
                            Terms and Conditions
                          </Link>
                        </p>
                        {faqVariant === "collection-kiosk" && (
                          <p>
                            Any items not accepted at a collection kiosk can be
                            taken home.
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </Accordion.Item>

                  {faqVariant === "drop-off-station" && (
                    <Accordion.Item
                      className="gap-0 border border-[#e4e4e4]"
                      value="faq-5b"
                    >
                      <AccordionTrigger className="justify-between w-full px-4 py-5 flex items-center">
                        <div className="text-2xl">
                          What happens if LEGO bricks are not accepted during
                          drop-off at a LEGO Store?
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="bg-gray-300/25 p-4">
                        <div className="flex flex-col space-y-4 py-x-large">
                          <p>
                            Any items not accepted during drop-off can be taken
                            home.
                          </p>
                          <p>
                            <Link
                              to={{
                                pathname: t("urls.preparation_guidelines"),
                                search: `?${new URLSearchParams(queryParams).toString()}`,
                              }}
                              className="text-blue cursor-pointer"
                            >
                              Review what's accepted
                            </Link>{" "}
                            and the{" "}
                            <Link
                              to={{
                                pathname: t("urls.terms_and_conditions"),
                                search: `?${new URLSearchParams(queryParams).toString()}`,
                              }}
                              className="text-blue cursor-pointer"
                            >
                              Terms and Conditions
                            </Link>
                          </p>
                        </div>
                      </AccordionContent>
                    </Accordion.Item>
                  )}
                </>
              )}

              {/* Universal FAQs for all variants */}
              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-6"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    Where can I use my trade-in gift card?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    <p>LEGO® e-Gift Cards can be redeemed:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>In all LEGO stores in the U.S.</li>
                      <li>On LEGO.com</li>
                      <li>
                        By phone order in the same region where the gift card
                        was issued
                      </li>
                    </ul>
                    <p>
                      LEGO® e-Gift Cards can't be redeemed at LEGOLAND® Parks,
                      LEGOLAND Discovery Centers, or other retailers.
                    </p>
                    <p>
                      Got questions about your gift card? Reach out to{" "}
                      <a
                        href="https://www.lego.com/en-us/service"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue cursor-pointer"
                      >
                        LEGO.com Customer Service
                      </a>{" "}
                      or{" "}
                      <a
                        href="https://www.lego.com/en-us/service/help-topics/article/about-lego-gift-cards"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue cursor-pointer"
                      >
                        learn more here
                      </a>
                      .
                    </p>
                  </div>
                </AccordionContent>
              </Accordion.Item>

              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-7"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    How is this program different from past LEGO Brick Take Back
                    programs?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    <p>
                      This program is a little different than previous versions!
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        You'll need to create a new account (separate from your
                        LEGO® account)
                      </li>
                      <li>
                        Preparation and packaging rules have changed – See the
                        latest{" "}
                        <Link
                          to={{
                            pathname: t("urls.preparation_guidelines"),
                            search: `?${new URLSearchParams(queryParams).toString()}`,
                          }}
                          className="text-blue cursor-pointer"
                        >
                          Preparation Guidelines
                        </Link>
                        .
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </Accordion.Item>

              <Accordion.Item
                className="gap-0 border border-[#e4e4e4]"
                value="faq-8"
              >
                <AccordionTrigger className="justify-between w-full border-b border-gray-300 px-4 py-5 flex items-center">
                  <div className="text-2xl">
                    How is the LEGO Brick Take Back program connected to The
                    LEGO Group?
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-gray-300/25 p-4">
                  <div className="flex flex-col space-y-4 py-x-large">
                    <p>
                      The LEGO® Brick Take Back program is being operated for
                      and on behalf of the LEGO Group by Pentatonic Ltd.
                    </p>
                    <p>
                      For full legal details, see the{" "}
                      <Link
                        to={{
                          pathname: t("urls.terms_and_conditions"),
                          search: `?${new URLSearchParams(queryParams).toString()}`,
                        }}
                        className="text-blue cursor-pointer"
                      >
                        Terms and Conditions
                      </Link>
                      .
                    </p>
                  </div>
                </AccordionContent>
              </Accordion.Item>
            </Accordion.Root>
          </div>
        </div>
      </div>

      {!endOfPilot25 ? (
        <div className="pt-xxxx-large py-xx-large flex flex-col gap-xxxx-large">
          <div className="px-xx-large flex flex-col justify-center items-center space-y-x-large mb-xx-large">
            <h3 className="text-primary text-2xl font-bold md:text-4xl text-center">
              Turn your bricks into rewards
            </h3>
            <CtaButton additionalTracking={[EVENT_19]} />
          </div>

          <div className="border border-gray-300 rounded-xl flex relative max-w-2xl md:pr-[282px] mt-large p-1 mx-6 md:mx-auto mb-xxxx-large">
            <div className="p-x-large flex flex-col gap-regular w-full space-y-small">
              <h3 className="text-primary text-2xl font-bold md:text-4xl">
                Have another question?
              </h3>
              <p className="text-primary">
                Still have a question we haven’t answered? Get in touch — we’re
                here to help.
              </p>
              <a
                href="mailto:Support@LEGOBrickTakeBack.com"
                className="select-none flex items-center justify-center whitespace-nowrap rounded-full font-medium ring-offset-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-gray-950 dark:focus-visible:ring-gray-300 bg-blue hover:bg-blue-600 text-white disabled:bg-blue-300 w-fit px-[24px] py-regular leading-7"
              >
                Contact Us
              </a>
            </div>
            <img
              src={LegoWoman}
              alt="Smiling LEGO Minifigure"
              className="md:absolute md:right-large md:bottom-0 w-[282px] md:w-[246px] hidden sm:block"
            />
          </div>
        </div>
      ) : (
        <div className="py-xxx-large"></div>
      )}
      <Footer />
    </div>
  );
};

export default FAQ;
